<template>
  <div class="promotion-table-container">
    <!-- 顶部搜索栏 -->
    <div class="search-bar">
      <form class="search-form">
        <div class="form-item">
          <label>用户ID:</label>
          <input
            v-model="searchForm.userId"
            type="text"
            placeholder="请输入用户ID"
            class="form-input"
          />
        </div>
        <div class="form-item">
          <label>状态:</label>
          <select v-model.number="searchForm.ifClaimed" class="form-select">
            <option value="">全部</option>
            <option :value="0">待审核</option>
            <option :value="1">已通过</option>
            <option :value="2">已拒绝</option>
          </select>
        </div>
        <div class="form-actions">
          <button
            type="button"
            @click="searchPromotions"
            class="btn btn-primary"
          >
            搜索
          </button>
          <button type="button" @click="clearSearch" class="btn btn-default">
            清空
          </button>
        </div>
      </form>
    </div>

    <!-- 推广记录表格 -->
    <div class="table-container">
      <div class="table-header">
        <h3>推广记录列表</h3>
        <div class="table-actions">
          <button @click="refreshData" class="btn btn-success">🔄 刷新</button>
        </div>
      </div>

      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">加载中...</div>
      </div>

      <div v-else-if="promotionList.length === 0" class="empty-container">
        <div class="empty-icon">📋</div>
        <div class="empty-text">暂无推广记录</div>
      </div>

      <div v-else class="table-wrapper">
        <table class="promotion-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>用户ID</th>
              <th>推广链接</th>
              <th>投稿时间</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in promotionList" :key="item.id" class="table-row">
              <td>{{ item.id }}</td>
              <td>{{ item.userId }}</td>
              <td class="link-cell">
                <span class="promotion-text" :title="item.promotionLink">
                  {{ truncateUrl(item.promotionLink) }}
                </span>
              </td>
              <td>{{ formatDate(item.createTime) }}</td>
              <td>
                <span
                  class="status-tag"
                  :class="getStatusClass(item.ifClaimed)"
                >
                  {{ getStatusText(item.ifClaimed) }}
                </span>
              </td>
              <td class="action-cell">
                <div class="action-buttons">
                  <button
                    v-if="item.ifClaimed === 0"
                    @click="approvePromotion(item)"
                    class="btn btn-success btn-small"
                    :disabled="processing || processingItems.has(item.id)"
                  >
                    {{
                      processingItems.has(item.id) &&
                      processingType === "approve"
                        ? "处理中..."
                        : "通过"
                    }}
                  </button>
                  <button
                    v-if="item.ifClaimed === 0"
                    @click="rejectPromotion(item)"
                    class="btn btn-danger btn-small"
                    :disabled="processing || processingItems.has(item.id)"
                  >
                    {{
                      processingItems.has(item.id) &&
                      processingType === "reject"
                        ? "处理中..."
                        : "拒绝"
                    }}
                  </button>
                  <button
                    @click="viewDetails(item)"
                    class="btn btn-info btn-small"
                  >
                    详情
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div v-if="total > 0" class="pagination-container">
        <div class="pagination-info">
          共 {{ total }} 条记录，第 {{ currentPage }} / {{ totalPages }} 页
        </div>
        <div class="pagination-controls">
          <button
            @click="previousPage"
            :disabled="currentPage <= 1"
            class="btn btn-default btn-small"
          >
            上一页
          </button>
          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            class="btn btn-default btn-small"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 详情弹窗 -->
    <div
      v-if="detailDialogVisible"
      class="modal-overlay"
      @click="closeDetailDialog"
    >
      <div class="modal-dialog large" @click.stop>
        <div class="modal-header">
          <h3>推广详情</h3>
          <button type="button" class="modal-close" @click="closeDetailDialog">
            ×
          </button>
        </div>
        <div class="modal-body">
          <div v-if="selectedPromotion" class="detail-content">
            <div class="detail-row">
              <label>记录ID:</label>
              <span>{{ selectedPromotion.id }}</span>
            </div>
            <div class="detail-row">
              <label>用户ID:</label>
              <span>{{ selectedPromotion.userId }}</span>
            </div>
            <div class="detail-row">
              <label>推广链接:</label>
              <div class="link-display">
                {{ selectedPromotion.promotionLink }}
              </div>
            </div>
            <div class="detail-row">
              <label>投稿时间:</label>
              <span>{{ formatDate(selectedPromotion.createTime) }}</span>
            </div>
            <div class="detail-row">
              <label>状态:</label>
              <span
                class="status-tag"
                :class="getStatusClass(selectedPromotion.ifClaimed)"
              >
                {{ getStatusText(selectedPromotion.ifClaimed) }}
              </span>
            </div>
            <div v-if="selectedPromotion.remark" class="detail-row">
              <label>备注:</label>
              <span>{{ selectedPromotion.remark }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-default"
            @click="closeDetailDialog"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { promotion } from "../../../api/index";

export default {
  name: "PromotionTable",
  data() {
    return {
      loading: false,
      processing: false,
      processingItems: new Set(), // 跟踪正在处理的项目ID
      processingType: "", // 跟踪处理类型 'approve' 或 'reject'
      searchForm: {
        userId: "",
        ifClaimed: "",
        pageSize: 10,
        pageNum: 1,
      },
      promotionList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      detailDialogVisible: false,
      selectedPromotion: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
  },
  mounted() {
    this.loadPromotions();
  },
  methods: {
    // 加载推广记录
    async loadPromotions() {
      this.loading = true;
      try {
        const response = await promotion.getPromotion(this.searchForm);
        if (response.code === 200) {
          this.promotionList = response.data.data || [];
          this.total = response.data.total || 0;
          this.pageSize = response.data.pageSize || 10;
        } else {
          this.$message.error(response.msg || "加载失败");
        }
      } catch (error) {
        console.error("加载推广记录失败:", error);
        this.$message.error("加载失败，请重试");
      } finally {
        this.loading = false;
      }
    },

    // 搜索推广记录
    searchPromotions() {
      this.currentPage = 1;
      this.searchForm.pageNum = 1;
      this.loadPromotions();
    },

    // 清空搜索
    clearSearch() {
      this.searchForm = {
        userId: "",
        ifClaimed: "",
        pageSize: 10,
        pageNum: 1,
      };
      this.currentPage = 1;
      this.loadPromotions();
    },

    // 刷新数据
    refreshData() {
      this.loadPromotions();
    },

    // 分页方法
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.searchForm.pageNum = this.currentPage;
        this.loadPromotions();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.searchForm.pageNum = this.currentPage;
        this.loadPromotions();
      }
    },

    // 通过推广
    async approvePromotion(item) {
      // 防止重复点击
      if (this.processingItems.has(item.id)) {
        return;
      }

      this.processing = true;
      this.processingItems.add(item.id);
      this.processingType = "approve";

      try {
        const response = await promotion.markPromotion({
          id: item.id,
          userId: item.userId,
          ifClaimed: 1,
        });
        if (response.code === 200) {
          this.$message.success("审核通过");
          this.loadPromotions();
        } else {
          this.$message.error(response.msg || "操作失败");
        }
      } catch (error) {
        console.error("审核失败:", error);
        this.$message.error("操作失败，请重试");
      } finally {
        this.processing = false;
        this.processingItems.delete(item.id);
        this.processingType = "";
      }
    },

    // 拒绝推广
    async rejectPromotion(item) {
      // 防止重复点击
      if (this.processingItems.has(item.id)) {
        return;
      }

      this.processing = true;
      this.processingItems.add(item.id);
      this.processingType = "reject";

      try {
        const response = await promotion.markPromotion({
          id: item.id,
          userId: item.userId,
          ifClaimed: 2,
        });
        if (response.code === 200) {
          this.$message.success("已拒绝");
          this.loadPromotions();
        } else {
          this.$message.error(response.msg || "操作失败");
        }
      } catch (error) {
        console.error("拒绝失败:", error);
        this.$message.error("操作失败，请重试");
      } finally {
        this.processing = false;
        this.processingItems.delete(item.id);
        this.processingType = "";
      }
    },

    // 查看详情
    viewDetails(item) {
      this.selectedPromotion = item;
      this.detailDialogVisible = true;
    },

    // 关闭详情弹窗
    closeDetailDialog() {
      this.detailDialogVisible = false;
      this.selectedPromotion = null;
    },

    // 工具方法
    truncateUrl(url) {
      if (!url) return "";
      if (url.length <= 80) return url;
      return url.substring(0, 77) + "...";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("zh-CN");
    },

    getStatusClass(ifClaimed) {
      const statusMap = {
        0: "status-pending",
        1: "status-approved",
        2: "status-rejected",
      };
      return statusMap[ifClaimed] || "status-default";
    },

    getStatusText(ifClaimed) {
      const statusMap = {
        0: "待审核",
        1: "已通过",
        2: "已拒绝",
      };
      return statusMap[ifClaimed] || "未知";
    },
  },
};
</script>

<style scoped>
.promotion-table-container {
  background: #faf9de;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 搜索栏样式 */
.search-bar {
  background: #ffffff;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-form {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.form-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-item label {
  font-weight: bold;
  color: #333;
  white-space: nowrap;
}

.form-input,
.form-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 150px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.form-actions {
  display: flex;
  gap: 10px;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-primary {
  background: #ffd700;
  color: #000;
}

.btn-primary:hover {
  background: #ffed4e;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #218838;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-warning {
  background: #ffc107;
  color: #000;
}

.btn-warning:hover {
  background: #e0a800;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover {
  background: #138496;
}

.btn-default {
  background: #6c757d;
  color: white;
}

.btn-default:hover {
  background: #5a6268;
}

.btn-small {
  padding: 4px 8px;
  font-size: 12px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 表格容器 */
.table-container {
  background: #ffffff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

.table-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.table-actions {
  display: flex;
  gap: 10px;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #666;
  font-size: 14px;
}

/* 空状态 */
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  color: #666;
  font-size: 16px;
}

/* 表格样式 */
.table-wrapper {
  flex: 1;
  overflow: auto;
}

.promotion-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.promotion-table th,
.promotion-table td {
  padding: 12px 8px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.promotion-table th {
  background: #f8f9fa;
  font-weight: bold;
  color: #333;
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-row:hover {
  background: #f8f9fa;
}

/* 推广内容样式 */
.link-cell {
  width: 300px;
  max-width: 300px;
  min-width: 300px;
}

.promotion-text {
  color: #333;
  font-size: 14px;
  line-height: 1.4;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 状态标签 */
.status-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-approved {
  background: #d4edda;
  color: #155724;
}

.status-rejected {
  background: #f8d7da;
  color: #721c24;
}

.status-default {
  background: #e2e3e5;
  color: #383d41;
}

/* 操作按钮 */
.action-cell {
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

/* 分页 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 10px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-dialog.large {
  max-width: 700px;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

/* 表单样式 */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: bold;
  color: #333;
}

.form-textarea {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
}

.form-textarea:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.link-display {
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 6px;
  word-break: break-all;
  font-size: 14px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row label {
  font-weight: bold;
  color: #333;
  min-width: 100px;
  flex-shrink: 0;
}

.detail-row span {
  color: #666;
  flex: 1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-form {
    flex-direction: column;
    align-items: stretch;
  }

  .form-item {
    flex-direction: column;
    align-items: stretch;
  }

  .form-input,
  .form-select {
    min-width: auto;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .promotion-table {
    min-width: 600px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .pagination-container {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
}
</style>
